import { Form, Formik } from 'formik';
import { TFunction } from 'i18next';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AppDispatch } from 'store/store';
import UDFormInput from 'ud-form/components/input';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import * as yup from 'yup';
import { StageType } from 'modules/championship/domain/enums/stage-type';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';
import { deleteStage } from 'modules/championship/store/stages/stages.actions';
import { useUpsertStage } from 'modules/championship/ui/hooks/use-upsert-stage';
import { Pages } from 'modules/navigation/domain/enums/pages';

interface StageInfoFormValues {
  id?: number;
  name: string;
  type?: SelectOption;
}

const stageValidator: yup.SchemaOf<StageInfoFormValues> = yup.object().shape({
  id: yup.number().optional(),
  name: yup.string().required('Название этапа обязательно'),
  type: yup
    .object({
      label: yup.string().required('Тип этапа обязателен'),
      value: yup.string().oneOf(Object.values(StageType)),
    })
    .default(undefined)
    .required('Тип этапа обязателен'),
});

const placesOptions: SelectOption[] = [
  { label: 1, value: 1 },
  { label: 2, value: 2 },
  { label: 3, value: 3 },
  { label: 4, value: 4 },
  { label: 5, value: 5 },
];

const prepareTypeOptions = (t: TFunction): SelectOption[] => {
  return Object.values(StageType).map((key) => ({
    label: t(`roundType.${key}`),
    value: key,
  }));
};

const gridOptions: SelectOption[] = [
  {
    value: 1,
    label: 'Исходя из списка команд',
  },
];

type StageInfoFormProps = {
  stage: StageV2 | null;
  championshipId: number;
};

const StageInfoForm = ({ championshipId, stage }: StageInfoFormProps) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const upsertStage = useUpsertStage();
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);

  const typeOptions = prepareTypeOptions(t);
  const gridOption = {
    value: 1,
    label: 'Исходя из списка команд',
  };

  const initialValues = stage
    ? {
        id: stage.id,
        name: stage.name,
        type: {
          value: stage.type,
          label: t(`roundType.${stage.type}`),
        },
        grid: gridOption,
      }
    : { name: '', grid: gridOption };

  const handleSubmit = useCallback(
    (values: StageInfoFormValues) => {
      setIsSaving(true);

      upsertStage({
        id: values.id,
        championshipId,
        name: values.name,
        type: values.type!.value as StageType,
        teamIds: stage?.teams.map((team) => team.id),
      })
        .then((updatedStage) => {
          if (!updatedStage) return;

          // Создание: уходим на адрес нового этапа. `replace` — чтобы «Назад» вёл
          // на список, а не обратно на пустую форму создания.
          if (!stage) {
            navigate(Pages.CHAMPIONSHIPS.STEPS.builders.stage(championshipId, updatedStage.id), { replace: true });
          }
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
    [championshipId, upsertStage, navigate, stage],
  );

  const handleDeleteStageClick = useCallback(() => {
    if (!stage?.id) {
      return;
    }

    setIsDeleting(true);

    dispatch(deleteStage(stage.id))
      .then(() => {
        navigate(Pages.CHAMPIONSHIPS.STEPS.builders.index(championshipId), { replace: true });
      })
      .finally(() => {
        setIsDeleting(false);
      });
  }, [championshipId, dispatch, navigate, stage?.id]);

  return (
    <Formik enableReinitialize={true} initialValues={initialValues} validationSchema={stageValidator} onSubmit={handleSubmit}>
      {(formProps) => {
        return (
          <Form onSubmit={formProps.handleSubmit}>
            <div className="row gap-y-6">
              <div className="w-7/12">
                <UDFormInput autoFocus={!Boolean(stage)} name="name" label="Название*" placeholder="Название этапа" />
              </div>
              <div className="w-7/12">
                <div className="grid grid-cols-12 gap-x-6">
                  <div className="col-span-6">
                    <UDFormSelect name="type" label="Тип этапа*" placeholder="Выберите этап" options={typeOptions} />
                  </div>
                  <div className="col-span-6">
                    <UDFormSelect name="place" label="Игра за место" placeholder="Выберите место" options={placesOptions} />
                  </div>
                </div>
              </div>
              <div className="w-7/12">
                <UDFormSelect name="grid" label="Размер сетки" placeholder="Выберите размер сетки" options={gridOptions} />
              </div>
              <div className="w-7/12">
                <div className="row">
                  <UDButton
                    variant="primary"
                    type="submit"
                    disabled={!formProps.dirty || !formProps.isValid || isSaving}
                    loading={isSaving}
                    className="mt-3 mb-3 w-full"
                  >
                    Сохранить
                  </UDButton>
                </div>
                {stage?.id && (
                  <div className="row">
                    <UDButton
                      className="mt-3 mb-3 w-full"
                      variant="secondary"
                      type="button"
                      disabled={isSaving || isDeleting}
                      loading={isDeleting}
                      onClick={openDeleteModal}
                    >
                      Удалить
                    </UDButton>
                  </div>
                )}
              </div>
            </div>
            <Modal.Confirm
              show={isDeleteModalOpen}
              title={stage?.name ? `Удалить этап «${stage.name}»?` : 'Удалить этап?'}
              description="Это действие необратимо."
              isLoading={isDeleting}
              onConfirm={handleDeleteStageClick}
              onCancel={closeDeleteModal}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default StageInfoForm;
